/**
 * Visualization helper functions for energy data
 * Chart data preparation and display utilities
 */

import {
  Chronotype,
  EnergyLevel,
  EnergyForecast,
  EnergyState,
  CHRONOTYPE_PROFILES,
  BaselineInput,
  WearableInput,
  UserEnergySettings
} from './types';

import { getEnergyBlock } from './blocks';
import { getBaselineEnergy } from './circadian';
import { convertBaselineInputToSleepData, parseChronotype } from './utils';
import { AdaptiveEnergySettingsService } from './adaptive-settings';

export interface ChartDataPoint {
  x: Date;
  y: number;
  label?: string;
  color?: string;
  state?: EnergyState;
}

export interface EnergyChart {
  title: string;
  data: ChartDataPoint[];
  insights: string[];
  recommendations: string[];
}

/**
 * Main getEnergyForecast function - ENHANCED with adaptive settings
 */
export async function getEnergyForecast(
  baselineInput?: BaselineInput,
  wearableInput?: WearableInput,
  startHour: number = 0,
  settings?: UserEnergySettings,
  wearableMetrics?: any[],
  connections?: any[]
): Promise<number[]> {
  console.log("getEnergyForecast (visualization.ts) called with adaptive enhancement");
  
  try {
    let finalBaselineInput: BaselineInput;

    // Use adaptive settings if available
    if (wearableMetrics || connections) {
      console.log('🔄 Getting adaptive energy settings for forecast...');
      const adaptiveSettings = await AdaptiveEnergySettingsService.getAdaptiveSettings(
        wearableMetrics, 
        connections
      );

      console.log(`📊 Using ${adaptiveSettings.source} settings for forecast:`, {
        wakeTime: adaptiveSettings.wakeTime,
        sleepTime: adaptiveSettings.sleepTime,
        confidence: adaptiveSettings.confidence,
        provider: adaptiveSettings.wearableProvider
      });

      // Convert adaptive settings to BaselineInput format
      finalBaselineInput = AdaptiveEnergySettingsService.toEnergyCalculationFormat(adaptiveSettings);
    } else if (baselineInput) {
      // Use provided baseline input
      finalBaselineInput = baselineInput;
    } else {
      // Default fallback
      console.log('⚠️ No adaptive or baseline input provided, using defaults');
      finalBaselineInput = {
        wakeTimeHour: 7,
        sleepDuration: 8,
        useShiftedCurve: true
      };
    }
    
    // Generate energy array using the baseline input
    const energyArray = getBaselineEnergy(finalBaselineInput) as unknown as number[];
    
    // Apply wearable adjustments if provided
    if (wearableInput) {
      console.log('Applying wearable adjustments to energy array');
      for (let i = 0; i < energyArray.length; i++) {
        energyArray[i] = applyWearableAdjustments(energyArray[i], wearableInput);
      }
    }
    
    // Apply bounds to ensure 0-100 range
    const finalArray = energyArray.map(energy => Math.max(0, Math.min(100, energy)));
    
    console.log("Final adaptive energy levels:", finalArray.slice(0, 8), "...");
    return finalArray;
    
  } catch (error) {
    console.error("Error in adaptive getEnergyForecast:", error);
    // Return a fallback curve
    return Array(24).fill(50);
  }
}

/**
 * Legacy sync version for backward compatibility
 */
export function getEnergyForecastSync(
  baselineInput: BaselineInput,
  wearableInput?: WearableInput,
  startHour: number = 0,
  settings?: UserEnergySettings
): number[] {
  // Only log in development to reduce console noise
  if (process.env.NODE_ENV === 'development') {
    console.log("getEnergyForecastSync (visualization.ts) called - legacy mode");
  }
  
  try {
    const energyArray = getBaselineEnergy(baselineInput) as unknown as number[];
    
    // Apply wearable adjustments if provided
    if (wearableInput) {
      for (let i = 0; i < energyArray.length; i++) {
        energyArray[i] = applyWearableAdjustments(energyArray[i], wearableInput);
      }
    }
    
    return energyArray.map(energy => Math.max(0, Math.min(100, energy)));
    
  } catch (error) {
    console.error("Error in sync getEnergyForecast:", error);
    return Array(24).fill(50);
  }
}

/**
 * Apply wearable data adjustments to base energy
 */
function applyWearableAdjustments(baseEnergy: number, wearableInput: WearableInput): number {
  let adjustment = 0;
  
  // Sleep quality adjustments
  if (wearableInput.sleepScore) {
    const sleepBonus = (wearableInput.sleepScore - 75) * 0.2;
    adjustment += Math.max(-10, Math.min(10, sleepBonus));
  }
  
  // HRV adjustments
  if (wearableInput.hrvScore) {
    const hrvBonus = (wearableInput.hrvScore - 50) * 0.15;
    adjustment += Math.max(-8, Math.min(8, hrvBonus));
  }
  
  // Readiness adjustments
  if (wearableInput.readinessScore) {
    const readinessBonus = (wearableInput.readinessScore - 75) * 0.15;
    adjustment += Math.max(-8, Math.min(8, readinessBonus));
  }
  
  // Recovery adjustments
  if (wearableInput.recoveryScore) {
    const recoveryBonus = (wearableInput.recoveryScore - 75) * 0.1;
    adjustment += Math.max(-5, Math.min(5, recoveryBonus));
  }
  
  return baseEnergy + adjustment;
}

/**
 * Create energy visualization chart data
 */
export function createEnergyVisualization(
  forecast: EnergyForecast,
  options: {
    includeStates?: boolean;
    includeBlocks?: boolean;
    highlightPeaks?: boolean;
    highlightDips?: boolean;
  } = {}
): EnergyChart {
  const data: ChartDataPoint[] = [];
  const insights: string[] = [];
  const recommendations: string[] = [];

  // Convert forecast to chart data
  forecast.data.forEach((prediction, index) => {
    const point: ChartDataPoint = {
      x: prediction.timestamp || new Date(),
      y: prediction.value,
      state: prediction.state
    };

    // Add color coding
    if (options.includeStates) {
      switch (prediction.state) {
        case EnergyState.ASLEEP:
          point.color = '#1e3a8a'; // Dark blue
          break;
        case EnergyState.PEAK:
          point.color = '#16a34a'; // Green
          break;
        case EnergyState.AWAKE_HIGH:
          point.color = '#84cc16'; // Light green
          break;
        case EnergyState.AWAKE_MODERATE:
          point.color = '#eab308'; // Yellow
          break;
        case EnergyState.AWAKE_LOW:
          point.color = '#dc2626'; // Red
          break;
      }
    }

    // Add block type labels
    if (options.includeBlocks && prediction.state !== EnergyState.ASLEEP) {
      const energyBlock = getEnergyBlock(prediction.energy || prediction.value);
      point.label = energyBlock.label;
    }

    data.push(point);
  });

  // Highlight peaks
  if (options.highlightPeaks && forecast.peak_times) {
    forecast.peak_times.forEach(peakTime => {
      const dataPoint = data.find(d => d.x.getTime() === peakTime.getTime());
      if (dataPoint) {
        dataPoint.color = '#059669'; // Emerald
        dataPoint.label = (dataPoint.label || '') + ' PEAK';
      }
    });
  }

  // Highlight dips  
  if (options.highlightDips && forecast.dip_times) {
    forecast.dip_times.forEach(dipTime => {
      const dataPoint = data.find(d => d.x.getTime() === dipTime.getTime());
      if (dataPoint) {
        dataPoint.color = '#dc2626'; // Red
        dataPoint.label = (dataPoint.label || '') + ' DIP';
      }
    });
  }

  // Generate insights
  const chronotype = forecast.chronotype || Chronotype.BEAR;
  const profile = CHRONOTYPE_PROFILES[chronotype];
  insights.push(`${profile.name} chronotype pattern detected`);

  if (forecast.peak_times && forecast.peak_times.length > 0) {
    const firstPeak = forecast.peak_times[0];
    insights.push(`Peak energy at ${firstPeak.toLocaleTimeString()}`);
  }

  if (forecast.dip_times && forecast.dip_times.length > 0) {
    const firstDip = forecast.dip_times[0];
    insights.push(`Energy dip at ${firstDip.toLocaleTimeString()}`);
  }

  const awakeEnergy = forecast.hourly_predictions?.filter(p => p.state !== EnergyState.ASLEEP) || [];
  if (awakeEnergy.length > 0) {
    const avgEnergy = awakeEnergy.reduce((sum, p) => sum + (p.energy || p.value), 0) / awakeEnergy.length;
    insights.push(`Average energy: ${Math.round(avgEnergy)}%`);
  }

  // Generate recommendations
  if (forecast.optimal_sleep_window) {
    recommendations.push(`Optimal sleep window: ${forecast.optimal_sleep_window[0].toLocaleTimeString()} - ${forecast.optimal_sleep_window[1].toLocaleTimeString()}`);
  }

  if ((forecast.sleep_debt_projection || 0) > 3) {
    recommendations.push('Consider prioritizing sleep recovery');
  }

  return {
    title: `Energy Forecast - ${profile.name} Chronotype`,
    data,
    insights,
    recommendations
  };
}

/**
 * Legacy function: Get forecast for day with basic visualization data
 */
export function getForecastForDay(
  energyLevels: number[],
  startTime: Date = new Date()
): ChartDataPoint[] {
  return energyLevels.map((energy, index) => ({
    x: new Date(startTime.getTime() + index * 60 * 60 * 1000),
    y: energy
  }));
}

/**
 * Get current energy zone visualization
 */
export function getCurrentEnergyZone(
  currentTime: Date,
  energyLevel: number,
  chronotype: Chronotype
): {
  blockType: string;
  energy: number;
  color: string;
  description: string;
  timeInBlock: string;
} {
  const energyBlock = getEnergyBlock(energyLevel);

  return {
    blockType: energyBlock.type,
    energy: energyLevel,
    color: energyBlock.color,
    description: energyBlock.description,
    timeInBlock: currentTime.toLocaleTimeString()
  };
}

/**
 * Generate energy zones throughout the day
 */
export function getEnergyZonesVisualization(
  forecast: EnergyForecast
): Array<{
  startTime: Date;
  endTime: Date;
  blockType: string;
  energy: number;
  color: string;
  duration: number;
}> {
  const blocks = [];
  let currentBlock = null;

  const predictions = forecast.hourly_predictions || forecast.data || [];

  for (let i = 0; i < predictions.length; i++) {
    const prediction = predictions[i];
    const energyBlock = prediction.state === EnergyState.ASLEEP ? 
      { type: 'sleep', color: '#1e3a8a' } : 
      getEnergyBlock(prediction.energy || prediction.value);

    if (!currentBlock || currentBlock.blockType !== energyBlock.type) {
      // Start new block
      if (currentBlock) {
        currentBlock.endTime = prediction.timestamp || new Date();
        currentBlock.duration = (currentBlock.endTime.getTime() - currentBlock.startTime.getTime()) / (1000 * 60 * 60);
        blocks.push(currentBlock);
      }

      currentBlock = {
        startTime: prediction.timestamp || new Date(),
        endTime: prediction.timestamp || new Date(),
        blockType: energyBlock.type,
        energy: prediction.energy || prediction.value,
        color: energyBlock.color,
        duration: 0
      };
    } else {
      // Continue current block
      currentBlock.energy = Math.max(currentBlock.energy, prediction.energy || prediction.value);
    }
  }

  // Close final block
  if (currentBlock) {
    currentBlock.endTime = new Date(currentBlock.startTime.getTime() + 60 * 60 * 1000);
    currentBlock.duration = 1;
    blocks.push(currentBlock);
  }

  return blocks;
}

/**
 * Create chronotype comparison visualization data
 */
export function createChronotypeComparison(
  date: Date,
  chronotypes: Chronotype[] = Object.values(Chronotype)
): Array<{
  chronotype: Chronotype;
  name: string;
  peakTimes: number[];
  averageEnergy: number;
  sleepWindow: [number, number];
  color: string;
}> {
  const colors = {
    [Chronotype.BEAR]: '#84cc16',
    [Chronotype.LION]: '#f59e0b', 
    [Chronotype.WOLF]: '#8b5cf6',
    [Chronotype.DOLPHIN]: '#06b6d4'
  };

  return chronotypes.map(chronotype => {
    const profile = CHRONOTYPE_PROFILES[chronotype];

    return {
      chronotype,
      name: profile.name,
      peakTimes: [profile.energy_peak_start, profile.energy_peak_end],
      averageEnergy: 65, // Simplified average
      sleepWindow: [
        parseInt(profile.optimal_sleep.split(':')[0]),
        parseInt(profile.optimal_wake.split(':')[0])
      ],
      color: colors[chronotype]
    };
  });
}

/**
 * Generate insights from energy visualization
 */
export function generateEnergyInsights(
  forecast: EnergyForecast
): {
  pattern_type: string;
  energy_stability: 'high' | 'medium' | 'low';
  peak_quality: 'excellent' | 'good' | 'poor';
  recommendations: string[];
  chronotype_alignment: number;
} {
  const awakeEnergy = forecast.hourly_predictions?.filter(p => p.state !== EnergyState.ASLEEP) || [];
  const energyValues = awakeEnergy.map(p => p.energy || p.value || 0);

  // Calculate energy stability
  const mean = energyValues.reduce((sum, e) => sum + e, 0) / energyValues.length;
  const variance = energyValues.reduce((sum, e) => sum + Math.pow(e - mean, 2), 0) / energyValues.length;
  const stability = variance < 100 ? 'high' : variance < 300 ? 'medium' : 'low';

  // Assess peak quality
  const maxEnergy = Math.max(...energyValues);
  const peak_quality = maxEnergy >= 85 ? 'excellent' : maxEnergy >= 70 ? 'good' : 'poor';

  // Pattern classification
  const profile = CHRONOTYPE_PROFILES[forecast.chronotype || Chronotype.BEAR];
  let pattern_type = `${profile.name} chronotype`;

  const recommendations = [];

  if (peak_quality === 'poor') {
    recommendations.push('Consider improving sleep quality for better energy peaks');
  }

  if (stability === 'low') {
    recommendations.push('Work on maintaining more consistent energy levels');
  }

  if ((forecast.sleep_debt_projection || 0) > 5) {
    recommendations.push('Prioritize sleep recovery to improve overall energy');
  }

  // Calculate chronotype alignment (simplified)
  const chronotype_alignment = peak_quality === 'excellent' ? 90 : 
                              peak_quality === 'good' ? 75 : 60;

  return {
    pattern_type,
    energy_stability: stability,
    peak_quality,
    recommendations,
    chronotype_alignment
  };
}

/**
 * Enhanced energy forecast visualization with scientific insights
 */
export function createAdvancedVisualization(
  forecast: EnergyForecast,
  showHormones: boolean = false,
  showFactors: boolean = false
): {
  energyData: ChartDataPoint[];
  hormoneLevels?: Array<{ time: Date; cortisol: number; melatonin: number }>;
  energyFactors?: Array<{ time: Date; circadian: number; sleepPressure: number; sleepDebt: number }>;
  scientificInsights: string[];
} {
  const predictions = forecast.hourly_predictions || [];
  const energyData: ChartDataPoint[] = predictions.map(p => ({
    x: p.timestamp || new Date(),
    y: p.energy || p.value || 0,
    state: p.state,
    color: p.state === EnergyState.ASLEEP ? '#1e3a8a' : 
           (p.energy || p.value || 0) >= 80 ? '#16a34a' :
           (p.energy || p.value || 0) >= 60 ? '#84cc16' :
           (p.energy || p.value || 0) >= 40 ? '#eab308' : '#dc2626'
  }));

  let hormoneLevels;
  let energyFactors;

  if (showHormones && predictions.length > 0) {
    hormoneLevels = predictions.map(p => ({
      time: p.timestamp || new Date(),
      cortisol: p.factors?.cortisol || 0,
      melatonin: p.factors?.melatonin || 0
    }));
  }

  if (showFactors && predictions.length > 0) {
    energyFactors = predictions.map(p => ({
      time: p.timestamp || new Date(),
      circadian: p.factors?.circadianDrive || 0,
      sleepPressure: p.factors?.sleepPressure || 0,
      sleepDebt: p.factors?.sleepDebtImpact || 0
    }));
  }

  const scientificInsights = [
    `Based on ${CHRONOTYPE_PROFILES[forecast.chronotype || Chronotype.BEAR].name} chronotype research`,
    `SAFTE model predicts ${forecast.peak_times?.length || 0} energy peak(s)`,
    `Sleep debt impact: ${(forecast.sleep_debt_projection || 0).toFixed(1)} hours`,
    `Circadian rhythm optimization: ${(forecast.peak_times?.length || 0) > 0 ? 'Good' : 'Needs improvement'}`
  ];

  return {
    energyData,
    hormoneLevels,
    energyFactors,
    scientificInsights
  };
}
